import React, { memo, useState } from 'react';
import { useLang } from '../../i18n';
import { useDraft } from '../../hooks/useDraft';
import { useSaveFailed } from '../../hooks/useSaveFailed';
import { keepInView } from '../shared/dom';
import { QtyPill } from '../shared/QtyPill';
import { updateChecklistItem } from '../../data/checklists';

/**
 * Store field with a live dropdown of stores already used — type to filter, click to
 * pick, or enter a new one. Saves on pick / blur / Enter (never mid-typing), so a
 * partial name is never stored.
 */
function StoreInput({ value, stores, onCommit, placeholder, ariaLabel }) {
  const [draft, setDraft] = useDraft(value);
  const [open, setOpen] = useState(true);
  const q = draft.trim().toLowerCase();
  const matches = (q ? stores.filter(s => s.toLowerCase().includes(q)) : stores)
    .filter(s => s.toLowerCase() !== q).slice(0, 8);
  const commit = val => onCommit(String(val != null ? val : draft).trim());
  return (
    <div className="do-store">
      <input className="do-check__store" value={draft} placeholder={placeholder} autoFocus aria-label={ariaLabel}
        onChange={e => { setDraft(e.target.value); setOpen(true); }} onFocus={keepInView} onBlur={() => commit()}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { setOpen(false); onCommit(value || ''); }
        }} />
      {open && matches.length > 0 && (
        <div className="do-store__menu">
          {matches.map(s => (
            // onMouseDown (not onClick) so the pick registers before the input's blur fires.
            <button key={s} type="button" className="do-store__opt"
              onMouseDown={e => { e.preventDefault(); commit(s); }}>🏪 {s}</button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * One line on a checklist's active list: [○] name · qty · 🏪 · ✕. Tap the ○ (or the
 * name) → logged to history with today's date AND cleared off the list (undo from the
 * toast). The store hides behind 🏪 (Shopping only). ✕ takes it off the list WITHOUT
 * logging it (no longer needed). Memoized: callbacks take the item, so rows only
 * re-render when their own item changes.
 */
export const ChecklistRow = memo(function ChecklistRow({ item, config, stores, onComplete, onRemove }) {
  const { t, ln } = useLang();
  const failed = useSaveFailed();
  const [editStore, setEditStore] = useState(false);
  const verb = t(`checklist.verb.${config.id}`);
  const name = ln(item.name);
  const saveStore = v => {
    setEditStore(false);
    if (v !== (item.store || '')) updateChecklistItem(item.id, { store: v }).catch(failed);
  };
  return (
    <div className="do-check" style={{ '--cc': config.color }}>
      <div className="do-check__main">
        <button className="do-check__box" onClick={() => onComplete(item)}
          aria-label={t('checklist.markLog', { name, verb })} title={t('checklist.markLogTitle', { verb })} />
        <button className="do-check__name" onClick={() => onComplete(item)}>{name}</button>
        <QtyPill cls="do-check" value={item.qty} addLabel={t('checklist.addQty')} ariaLabel={t('checklist.qtyFor', { name })}
          onCommit={v => updateChecklistItem(item.id, { qty: v }).catch(failed)} />
        {config.hasStore && (
          <button className={`do-check__storebtn ${item.store ? 'do-check__storebtn--set' : 'do-check__storebtn--unset'}`}
            onClick={() => setEditStore(e => !e)} aria-label={t('checklist.editStore', { name })}
            title={item.store ? t('checklist.storeSet', { store: item.store }) : t('checklist.setStore')}>🏪</button>
        )}
        <button className="do-check__del" onClick={() => onRemove(item)} aria-label={t('checklist.removeItem', { name })}
          title={t('checklist.removeNoLog')}>✕</button>
      </div>
      {config.hasStore && editStore && (
        <StoreInput value={item.store} stores={stores} placeholder={t('checklist.whichStore')}
          ariaLabel={t('checklist.storeFor', { name })} onCommit={saveStore} />
      )}
    </div>
  );
});
